import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../../context/ThemeContext";
import { useUserForm } from "./hooks";
import { UserList, UserPagination, UserDetailsModal, ChangePasswordModal } from "./components";
import { UserFilters } from "./components/UserFilters";
import { UserViewToggle } from "./components/UserViewToggle";
import UserAddModal from "./components/UserAddModal";
import {
  useCompaniesStore,
  useDisableCRUD,
} from "../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../components/base/CRUDTooltip";
import { useRolesStore } from "../../../store/admin/rolesStore";
import { useStatusesStore } from "../../../store/admin/statusesStore";
import type { UserFormData } from "./components/UserForm";

import type { ThemeClasses, User } from "./types";
import useIsMonitor from "../../../hooks/useIsMonitor";
import {
  useMultiSelect,
  BulkActionBar,
  type BulkAction,
} from "../../../Layouts/reseller-admin/shared";

export default function UsersSection() {
  const { t } = useTranslation("adminSections");
  const { isDarkMode } = useDarkMode();

  // Companies, Roles and Statuses stores
  const allCompanies = useCompaniesStore((state) => state.allCompanies);
  const allCompaniesLoading = useCompaniesStore(
    (state) => state.allCompaniesLoading,
  );
  const fetchAllCompanies = useCompaniesStore(
    (state) => state.fetchAllCompanies,
  );

  const roles = useRolesStore((state) => state.roles);
  const rolesLoading = useRolesStore((state) => state.loading);
  const fetchRoles = useRolesStore((state) => state.fetchRoles);

  const statuses = useStatusesStore((state) => state.statuses);
  const statusesLoading = useStatusesStore((state) => state.loading);
  const fetchStatuses = useStatusesStore((state) => state.fetchStatuses);

  // Cleanup scroll lock on unmount and fetch companies/roles/statuses
  useEffect(() => {
    fetchAllCompanies();
    if (statuses.length === 0) {
      fetchStatuses({ limit: 100 });
    }
    fetchRoles();
    return () => {
      document.body.style.overflow = "";
    };
  }, [fetchAllCompanies, fetchRoles, fetchStatuses, statuses.length]);

  // Obtener isAdminReseller del hook (usado más abajo)
  // Nota: Este hook debe ser llamado antes del useEffect de devices

  const {
    // Data
    users,
    meta,
    loading,
    selectedUser,

    // Modal states
    showAddModal,
    loadingSubmit,
    isEdit,

    // Filter states
    selectedStatus,
    searchQuery,
    currentPage,
    selectedView,

    // Handlers
    handleStatusChange,
    handleSearchChange,
    handleDetailsClick,
    handleEditClick,
    handleAddClick,
    setCurrentPage,
    setSelectedView,
    handleDeleteUser,
    handleHardDeleteUser,
    handleActivateUser,
    handleBulkDeactivateUsers,
    handleBulkActivateUsers,
    handleBulkHardDeleteUsers,
    handleCreateUser,
    handleUpdateUser,
    closeAddModal,
    isAdminReseller,
    isModalOpen,
    closeModal,
    handleChangePassword,
    handleConfirmPasswordChange,
    handleClosePasswordModal,
    showChangePasswordModal,
    userForPasswordChange,
  } = useUserForm();

  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    bgInput: isDarkMode ? "bg-dark-surface" : "bg-50",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  const totalPages = meta?.totalPages || 1;
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();

  // Multi-select functionality
  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    toggleSelectionMode,
    selectedCount,
    getSelectedItems,
  } = useMultiSelect<User>();

  const handleToggleSelection = useCallback(
    (user: User) => {
      toggleSelection(user.id);
    },
    [toggleSelection],
  );

  const handleBulkDeactivate = useCallback(async () => {
    const selectedUsers = getSelectedItems(users);
    await handleBulkDeactivateUsers(selectedUsers);
    clearSelection();
  }, [getSelectedItems, users, handleBulkDeactivateUsers, clearSelection]);

  const handleBulkActivate = useCallback(async () => {
    const selectedUsers = getSelectedItems(users);
    await handleBulkActivateUsers(selectedUsers);
    clearSelection();
  }, [getSelectedItems, users, handleBulkActivateUsers, clearSelection]);

  const handleBulkDelete = useCallback(async () => {
    const selectedUsers = getSelectedItems(users);
    await handleBulkHardDeleteUsers(selectedUsers);
    clearSelection();
  }, [getSelectedItems, users, handleBulkHardDeleteUsers, clearSelection]);

  const bulkActions: BulkAction[] = [
    {
      id: "activate",
      label: t("users.bulkActions.activate"),
      icon: "ri-check-line",
      onClick: handleBulkActivate,
      variant: "success",
    },
    {
      id: "deactivate",
      label: t("users.bulkActions.deactivate"),
      icon: "ri-close-circle-line",
      onClick: handleBulkDeactivate,
      variant: "warning",
    },
    {
      id: "delete",
      label: t("users.bulkActions.delete"),
      icon: "ri-delete-bin-line",
      onClick: handleBulkDelete,
      variant: "danger",
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div
        className={`${themeClasses.bg} ${themeClasses.border} p-4 sm:p-6 pb-0`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="min-w-0">
            <h1
              className={`text-xl sm:text-2xl font-bold ${themeClasses.text}`}
            >
              {t("users.title")}
            </h1>
            <p className={`text-sm ${themeClasses.textSecondary} mt-1`}>
              {t("users.subtitle")}
            </p>
          </div>
          {!isMonitor && !isReadOnly && (
            <CRUDTooltip className="self-start sm:self-auto flex-shrink-0">
              <button
                disabled={disableCRUD}
                onClick={handleAddClick}
                className="bg-primary-500 hover:bg-primary-400 text-gray-900 px-3 sm:px-4 py-2 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors whitespace-nowrap text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <i className="ri-add-line"></i>
                <span className="hidden sm:inline">{t("users.addUser")}</span>
                <span className="sm:hidden">Nuevo</span>
              </button>
            </CRUDTooltip>
          )}
        </div>
      </div>

      {/* Filters */}
      <div
        className={`${themeClasses.bg} border-b ${themeClasses.border} px-4 sm:px-6 pb-4 sm:pb-6`}
      >
        <div className="flex items-center justify-between flex-col md:flex-row md:gap-4 gap-2">
          <div className="flex items-center flex-col md:flex-row md:gap-4 gap-2 w-full md:w-auto">
            <UserFilters
              selectedStatus={selectedStatus}
              searchQuery={searchQuery}
              isDarkMode={isDarkMode}
              themeClasses={themeClasses}
              onStatusChange={handleStatusChange}
              onSearchChange={handleSearchChange}
            />
          </div>
          {/* View Toggle and Selection Mode */}
          <div className="flex items-center gap-2">
            {/* Selection Mode Toggle */}
            <button
              onClick={toggleSelectionMode}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${
                isSelectionMode
                  ? "bg-primary-500 text-gray-900"
                  : `${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`
              }`}
            >
              <i
                className={`${isSelectionMode ? "ri-checkbox-multiple-fill" : "ri-checkbox-multiple-line"}`}
              ></i>
              <span className="hidden sm:inline">
                {isSelectionMode
                  ? t("users.exitSelection")
                  : t("users.selectMultiple")}
              </span>
            </button>

            {/* Select All Button (only in selection mode) */}
            {isSelectionMode && (
              <button
                onClick={() => selectAll(users)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${themeClasses.buttonHover} border ${themeClasses.cardBorder} ${themeClasses.buttonText}`}
              >
                <i className="ri-checkbox-fill mr-1"></i>
                <span className="hidden sm:inline">{t("users.selectAll")}</span>
              </button>
            )}

            <UserViewToggle
              selectedView={selectedView}
              themeClasses={themeClasses}
              onViewChange={setSelectedView}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            <UserList
              users={users}
              themeClasses={themeClasses}
              isDarkMode={isDarkMode}
              selectedView={selectedView}
              onDetails={handleDetailsClick}
              onEdit={handleEditClick}
              onDelete={handleDeleteUser}
              onHardDelete={handleHardDeleteUser}
              onActivate={handleActivateUser}
              onChangePassword={handleChangePassword}
              isAdminReseller={isAdminReseller}
              isSelectionMode={isSelectionMode}
              selectedIds={selectedIds}
              onToggleSelection={handleToggleSelection}
            />

            {users.length > 0 && (
              <UserPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* User Add/Edit Modal */}
      <UserAddModal
        isOpen={showAddModal}
        isEdit={isEdit}
        editUser={selectedUser}
        isDarkMode={isDarkMode}
        themeClasses={themeClasses}
        companies={allCompanies}
        roles={roles}
        statuses={statuses}
        companiesLoading={allCompaniesLoading}
        rolesLoading={rolesLoading}
        statusesLoading={statusesLoading}
        loadingSubmit={loadingSubmit}
        onClose={closeAddModal}
        onSubmit={(data: UserFormData) => {
          if (isEdit) {
            handleUpdateUser(data);
          } else {
            handleCreateUser(data);
          }
        }}
      />

      {/* User Details Modal */}
      {isModalOpen && selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          onClose={closeModal}
          onEdit={(user) => {
            closeModal();
            handleEditClick(user);
          }}
          onDelete={
            isAdminReseller
              ? (user) => {
                  closeModal();
                  handleDeleteUser(user);
                }
              : undefined
          }
          onHardDelete={
            isAdminReseller
              ? (user) => {
                  closeModal();
                  handleHardDeleteUser(user);
                }
              : undefined
          }
          onActivate={
            isAdminReseller
              ? (user) => {
                  closeModal();
                  handleActivateUser(user);
                }
              : undefined
          }
          isAdminReseller={isAdminReseller}
        />
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && userForPasswordChange && (
        <ChangePasswordModal
          user={userForPasswordChange}
          themeClasses={themeClasses}
          onClose={handleClosePasswordModal}
          onConfirm={handleConfirmPasswordChange}
          loading={loadingSubmit}
        />
      )}

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedCount={selectedCount}
        actions={bulkActions}
        onClearSelection={clearSelection}
        isDarkMode={isDarkMode}
      />
    </div>
  );
}
